/**
 * 装上跨端埋点上下文的 web 半边 —— 三件事，缺一件链路就断一截。
 *
 * | 做什么 | 没做的后果 |
 * |---|---|
 * | 订阅 `tracking-context` 补推 | 预热 WebView 的 `nativeSessionId`、池复用后变化的 `traceId` 全丢 |
 * | 读 `window.__ED_TRACKING__` 写成 metadata | web session **无法按 `app_run_id` 筛出来**（envelope 只把它放进事件 payload，metadata 才是 OpenReplay 的筛选维度） |
 * | 回传自己的 sessionId | 原生侧 `web_or_session` 永远为空，跨端只能单向定位 |
 *
 * 这三件此前都只有函数、没有调用方（`applyNativeContext` / `mergeNativeContext`
 * 全仓零引用），属于"实现了一半"：原生按契约注入并监听，web 侧不接。
 *
 * ## 顺序是刻意的 —— 竞态在这里消除
 *
 * 原生在收到 `page-ready` 时就推 `tracking-context`，而 tracker 启动是异步的
 * （动态 import + 握手），很可能**晚于**那条推送。所以：
 *
 *   订阅       → 装在 tracker 之前（订阅不依赖 tracker），收到就并进全局，消息不会丢
 *   写 metadata → 等 tracker 就绪后**从全局现读一次**，把早到的补推一并写进去
 *
 * 反过来（先等 tracker 再订阅）就会丢掉早到的那条，而症状是"偶发缺
 * `native_or_session`"——只在预热 WebView 上出现，极难复现。
 *
 * 契约：`docs/engineering/native-web-bridge-contract.md` §3.2。
 */

import { getBridge } from "@/src/lib/bridge/bridge";
import type { NativeTrackingContext } from "./envelope";
import { Track } from "./index";
import {
  applyNativeContext,
  mergeNativeContext,
  nativeTrackingContext,
} from "./nativeContext";
import { reportWebSessionToNative } from "./nativeSession";

/** native→web 的补推消息（无 id 的 push，契约 §3.2）。 */
const CONTEXT_PUSH = "tracking-context";

/**
 * 第一步：订阅补推。**在 tracker 启动前调**。
 *
 * 返回退订函数。
 */
export function subscribeNativeTrackingContext(): () => void {
  try {
    return getBridge().on(CONTEXT_PUSH, (data: unknown) => {
      if (!data || typeof data !== "object") {
        return;
      }
      // 并进 window.__ED_TRACKING__；此刻 tracker 可能还没起来，
      // 里面的 metadata 写入会落空 —— 没关系，第二步会从全局现读补齐。
      mergeNativeContext(data as Partial<NativeTrackingContext>);
    });
  } catch (e) {
    Track.log("warn", "Tracking", "订阅 tracking-context 失败", {
      cause: String(e),
    });
    return () => {};
  }
}

/**
 * 第二步：tracker 就绪后调 —— 写 metadata + 回传 sessionId。
 *
 * 返回停止回传轮询的函数。
 */
export function applyNativeTrackingAfterStart(): () => void {
  try {
    applyNativeContext(nativeTrackingContext());
  } catch (e) {
    Track.log("warn", "Tracking", "应用原生埋点上下文失败", {
      cause: String(e),
    });
  }
  return reportWebSessionToNative();
}
